import { type ComponentProps, createElement, type ReactElement } from "react";

import { cn } from "./cn";
import { type IconName, iconSources } from "./icon-sources";

// The one icon of the console: a Hugeicons Pro icon (Rounded, Stroke) drawn by name, as in the Paper mock-ups. It takes its colour from
// the text colour of its parent (`currentColor`), so it follows the theme and the tone of a badge. It has one size scale. It is
// decoration by default (`aria-hidden`): the word next to it says what it says. Give `label` only for an icon that is the only label of
// a control; then it is an image with that name.

export type { IconName } from "./icon-sources";

/** The size scale, in px: a glyph in a line of text, an icon in a navigation row or a bar, an icon that leads a button or a header. */
const SIZES = { sm: 16, md: 18, lg: 20 } as const;
// The same sizes as a `size-*` class: a parent that sizes the icons it holds (a `Button`: `[&_svg:not([class*='size-'])]:size-4`) skips an icon
// that carries one, and a CSS rule would otherwise win over the width and height attributes.
const SIZE_CLASSES = { sm: "size-4", md: "size-4.5", lg: "size-5" } as const;
type IconSize = keyof typeof SIZES;

type Shape = Readonly<{ tag: "path" | "circle" | "rect"; props: Readonly<Record<string, string>> }>;

// The source files are drawn in one fixed dark ink. Every colour is replaced by the current text colour here, so no file
// is edited after the copy from the library.
const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;
const toReactName = (name: string) =>
  name.replace(/-(\w)/g, (_, letter: string) => letter.toUpperCase());

function parse(source: string): Shape[] {
  return Array.from(source.matchAll(/<(path|circle|rect)\s([^>]*?)\/>/g), (shape) => {
    const props: Record<string, string> = {};
    for (const attribute of (shape[2] as string).matchAll(/([a-z-]+)="([^"]*)"/g)) {
      const value = attribute[2] as string;
      props[toReactName(attribute[1] as string)] = HEX_COLOUR.test(value) ? "currentColor" : value;
    }
    return { tag: shape[1] as Shape["tag"], props };
  });
}

const shapes = new Map<IconName, Shape[]>();
function shapesOf(name: IconName): Shape[] {
  let found = shapes.get(name);
  if (found === undefined) {
    found = parse(iconSources[name]);
    shapes.set(name, found);
  }
  return found;
}

// Stroke props are left out on purpose: every shape of a source file carries its own stroke attributes, so a stroke prop on the root would do nothing.
type IconProps = Omit<
  ComponentProps<"svg">,
  | "children"
  | "aria-hidden"
  | "aria-label"
  | "stroke"
  | "strokeWidth"
  | "strokeLinecap"
  | "strokeLinejoin"
> &
  Readonly<{
    name: IconName;
    size?: IconSize;
    /** The accessible name, for an icon that is the only label of a control. Without it the icon is hidden from screen readers. */
    label?: string;
  }>;

export function Icon({ name, size = "sm", label, className, ...props }: IconProps): ReactElement {
  const px = SIZES[size];
  return (
    <svg
      viewBox="0 0 24 24"
      width={px}
      height={px}
      fill="none"
      focusable="false"
      className={cn("shrink-0", SIZE_CLASSES[size], className)}
      aria-hidden={label === undefined ? true : undefined}
      role={label === undefined ? undefined : "img"}
      aria-label={label}
      {...props}
    >
      {shapesOf(name).map(({ tag, props: shape }, index) =>
        createElement(tag, { key: index, ...shape }),
      )}
    </svg>
  );
}
